'use client';

import { useState } from 'react';
import { CompanyInfo } from '../lib/types';

interface Step2Props {
  company: CompanyInfo;
  onChange: (company: CompanyInfo) => void;
  onNext: () => void;
  onBack: () => void;
}

function getRecommendations(name: string) {
  const checks = [
    { label: 'Nom renseigné', ok: name.trim().length >= 2 },
    { label: 'Nom court (< 20 caractères)', ok: name.trim().length > 0 && name.trim().length <= 20 },
    { label: 'Pas de chiffres', ok: !/\d/.test(name) },
    { label: 'Pas de caractères spéciaux', ok: !/[^a-zA-ZÀ-ÿ\s\-']/.test(name) || name.length === 0 },
    { label: 'Facile à retenir', ok: name.trim().length >= 2 && name.trim().length <= 15 },
  ];
  return checks;
}

const SECTORS = [
  '', 'Technologie', 'Commerce', 'Finance', 'Santé', 'Éducation',
  'Immobilier', 'Transport', 'Agriculture', 'Restauration',
  'Conseil', 'Communication', 'Industrie', 'Autre',
];

export default function Step2CompanyInfo({ company, onChange, onNext, onBack }: Step2Props) {
  const [touched, setTouched] = useState(false);
  const recommendations = getRecommendations(company.name);
  const canProceed = company.name.trim().length >= 2;

  return (
    <div className="animate-fade-in max-w-lg mx-auto">
      <h2 className="text-2xl font-bold mb-2">Informations sur votre entreprise</h2>
      <p className="text-muted mb-8">
        Ces informations nous permettront de générer des noms de domaine adaptés.
      </p>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium mb-1.5">
            Nom de l&apos;entreprise <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={company.name}
            onChange={(e) => {
              onChange({ ...company, name: e.target.value });
              setTouched(true);
            }}
            placeholder="Ex: Koffi Technologies"
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground bg-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">
            Sigle / Acronyme <span className="text-muted text-xs">(optionnel)</span>
          </label>
          <input
            type="text"
            value={company.acronym}
            onChange={(e) => onChange({ ...company, acronym: e.target.value })}
            placeholder="Ex: KT"
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground bg-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">
            Secteur d&apos;activité <span className="text-muted text-xs">(optionnel)</span>
          </label>
          <select
            value={company.sector}
            onChange={(e) => onChange({ ...company, sector: e.target.value })}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground bg-white"
          >
            {SECTORS.map((s) => (
              <option key={s} value={s}>
                {s || 'Sélectionnez...'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {touched && (
        <div className="mt-6 bg-primary-light/50 rounded-lg p-4 animate-fade-in">
          <p className="text-sm font-semibold mb-3 text-primary">Recommandations</p>
          <ul className="space-y-1.5">
            {recommendations.map((r) => (
              <li key={r.label} className="flex items-center gap-2 text-sm">
                <span className={r.ok ? 'text-success' : 'text-danger'}>
                  {r.ok ? '✓' : '✗'}
                </span>
                <span className={r.ok ? 'text-foreground' : 'text-muted'}>{r.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-3 mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg border border-border text-muted hover:bg-gray-50 transition-colors cursor-pointer"
        >
          Retour
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="flex-1 bg-primary hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer"
        >
          Générer les domaines
        </button>
      </div>
    </div>
  );
}